import React, { createContext, useContext, useState, useRef, useCallback, useEffect, ReactNode } from "react";
import { Workflow, Agent, Event, WebSocketMessage } from "../../shared/types";
import { workflowApi } from "../services/api";
import { API_ENDPOINTS, debugLog, debugError } from "../../shared/config";

interface WorkflowState {
  workflow: Workflow | null;
  agents: Agent[];
  events: Event[];
  isConnected: boolean;
  isRunning: boolean;
}

interface WorkflowContextType extends WorkflowState {
  // WebSocket operations
  connectWebSocket: (workflowId: string) => void;
  disconnectWebSocket: () => void;
  
  // Workflow operations
  startWorkflow: () => Promise<void>;
  stopWorkflow: () => Promise<void>;
  updateWorkflow: (updates: Partial<Workflow>) => Promise<void>;
  
  // Agent operations
  updateAgent: (agentId: string, updates: Partial<Agent>) => void;
  
  // Event operations
  addEvent: (event: Event) => void;
  
  // Chat operations
  sendChatMessage: (message: string, targetAgents: string[]) => void;
  
  // Loading states
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

interface WorkflowProviderProps {
  children: ReactNode;
  workflow: Workflow | null;
  agents: Agent[];
  events: Event[];
  isConnected: boolean;
  isRunning: boolean;
  onWorkflowUpdate: (workflow: Workflow | null) => void;
  onAgentsUpdate: (agents: Agent[]) => void;
  onEventsUpdate: (events: Event[]) => void;
  onConnectionUpdate: (connected: boolean) => void;
  onRunningUpdate: (running: boolean) => void;
}

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({
  children,
  workflow: initialWorkflow,
  agents: initialAgents,
  events: initialEvents,
  isConnected: initialConnected,
  isRunning: initialRunning,
  onWorkflowUpdate,
  onAgentsUpdate,
  onEventsUpdate,
  onConnectionUpdate,
  onRunningUpdate,
}) => {
  const [workflow, setWorkflow] = useState<Workflow | null>(initialWorkflow);
  const [agents, setAgents] = useState<Agent[]>(initialAgents);
  const [events, setEvents] = useState<Event[]>(initialEvents);
  const [isConnected, setIsConnected] = useState(initialConnected);
  const [isRunning, setIsRunning] = useState(initialRunning);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);

  // Sync with parent state
  useEffect(() => {
    setWorkflow(initialWorkflow);
  }, [initialWorkflow]);

  useEffect(() => {
    setAgents(initialAgents);
  }, [initialAgents]);

  useEffect(() => {
    setEvents(initialEvents);
  }, [initialEvents]);

  useEffect(() => {
    setIsConnected(initialConnected);
  }, [initialConnected]);

  useEffect(() => {
    setIsRunning(initialRunning);
  }, [initialRunning]);

  // WebSocket connection management
  const connectWebSocket = useCallback((workflowId: string) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${API_ENDPOINTS.websocket}/${workflowId}?userId=user-${Date.now()}`;
    debugLog("Connecting to WebSocket:", wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      debugLog("WebSocket connected successfully");
      setIsConnected(true);
      onConnectionUpdate(true);
      setError(null);
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        debugLog("WebSocket message received:", message.type, message);
        handleWebSocketMessage(message);
      } catch (err) {
        debugError("Error parsing WebSocket message:", err);
      }
    };

    ws.onclose = () => {
      debugLog("WebSocket disconnected");
      setIsConnected(false);
      onConnectionUpdate(false);
    };

    ws.onerror = (error) => {
      debugError("WebSocket error:", error);
      setError("WebSocket connection failed");
    };

    wsRef.current = ws;
  }, [onConnectionUpdate]);

  const disconnectWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    console.log("📨 WebSocket message received:", message.type, message);
    switch (message.type) {
      case "workflow.state":
        if (message.data) {
          const data = message.data as {
            workflow: Workflow;
            agents: Agent[];
            events: Event[];
          };
          console.log("🏗️ Workflow state - Agents:", data.agents);
          console.log("🏗️ Workflow state - Events:", data.events);
          setWorkflow(data.workflow);
          onWorkflowUpdate(data.workflow);
          setAgents(data.agents || []);
          onAgentsUpdate(data.agents || []);
          setEvents(data.events || []);
          onEventsUpdate(data.events || []);
        }
        break;

      case "workflow.started":
        setWorkflow(message.data as Workflow);
        onWorkflowUpdate(message.data as Workflow);
        setIsRunning(true);
        onRunningUpdate(true);
        break;

      case "workflow.completed":
      case "workflow.failed":
      case "workflow.cancelled":
        setWorkflow(message.data as Workflow);
        onWorkflowUpdate(message.data as Workflow);
        setIsRunning(false);
        onRunningUpdate(false);
        break;

      case "agent.spawned":
        const agent = message.data as Agent;
        console.log("🤖 Agent spawned - Raw message:", message);
        console.log("🤖 Agent spawned - Agent data:", agent);
        setAgents(prev => {
          const newAgents = [
            ...prev.filter((a) => a.id !== agent.id),
            agent,
          ];
          onAgentsUpdate(newAgents);
          return newAgents;
        });
        break;

      case "agent.updated":
        const updatedAgent = message.data as Agent;
        setAgents(prev => {
          const newAgents = prev.map((a) =>
            a.id === updatedAgent.id ? { ...a, ...updatedAgent } : a,
          );
          onAgentsUpdate(newAgents);
          return newAgents;
        });
        break;

      case "event.created":
        setEvents(prev => {
          const newEvents = [...prev, message.data as Event];
          onEventsUpdate(newEvents);
          return newEvents;
        });
        break;

      case "connection.established":
        console.log("Connection established:", message.data);
        break;

      default:
        console.log("Unhandled message type:", message.type);
    }
  };

  // Workflow operations
  const startWorkflow = useCallback(async () => {
    if (!workflow) return;

    try {
      setLoading(true);
      debugLog("Starting workflow:", workflow.id);
      const result = await workflowApi.start(workflow.id);

      if (result.success && result.data) {
        setWorkflow(result.data);
        onWorkflowUpdate(result.data);
        setIsRunning(result.data.status === "running");
        onRunningUpdate(result.data.status === "running");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start workflow");
      debugError("Error starting workflow:", err);
    } finally {
      setLoading(false);
    }
  }, [workflow, onWorkflowUpdate, onRunningUpdate]);

  const stopWorkflow = useCallback(async () => {
    if (!workflow) return;

    try {
      await workflowApi.stop(workflow.id);
      console.log("Workflow stopped successfully");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to stop workflow");
      console.error("Error stopping workflow:", err);
    }
  }, [workflow]);

  const updateWorkflow = useCallback(async (updates: Partial<Workflow>) => {
    if (!workflow) return;

    try {
      const result = await workflowApi.update(workflow.id, updates);
      if (result.success && result.data) {
        const updatedWorkflow = { ...workflow, ...result.data };
        setWorkflow(updatedWorkflow);
        onWorkflowUpdate(updatedWorkflow);
      } else {
        setError(result.error || "Failed to update workflow");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update workflow");
    }
  }, [workflow, onWorkflowUpdate]);

  // Agent operations
  const updateAgent = useCallback((agentId: string, updates: Partial<Agent>) => {
    setAgents(prev => {
      const newAgents = prev.map((a) =>
        a.id === agentId ? { ...a, ...updates } : a,
      );
      onAgentsUpdate(newAgents);
      return newAgents;
    });
  }, [onAgentsUpdate]);

  // Event operations
  const addEvent = useCallback((event: Event) => {
    setEvents(prev => {
      const newEvents = [...prev, event];
      onEventsUpdate(newEvents);
      return newEvents;
    });
  }, [onEventsUpdate]);

  // Chat operations
  const sendChatMessage = useCallback(
    (message: string, targetAgents: string[]) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({ type: "chat.send", data: { message, targetAgents } }),
        );
      }
    },
    [],
  );

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const value: WorkflowContextType = {
    workflow,
    agents,
    events,
    isConnected,
    isRunning,
    connectWebSocket,
    disconnectWebSocket,
    startWorkflow,
    stopWorkflow,
    updateWorkflow,
    updateAgent,
    addEvent,
    sendChatMessage,
    loading,
    error,
    setError,
  };

  return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>;
};

export const useWorkflow = (): WorkflowContextType => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error("useWorkflow must be used within a WorkflowProvider");
  }
  return context;
};
